// Static tutorial content extracted from V1.1 BRAC Bank CMS Administration Manual.
// Frontend-only data — no backend.

const slugify = (s) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const raw = [
  {
    chapter: 1,
    chapterTitle: "System Access & Security",
    chapterAccent: "yellow",
    chapterIntro:
      "Get into the BRAC Bank CMS the right way — institutional credentials + OTP-based two-factor authentication.",
    sections: [
      {
        id: "1.1",
        title: "Accessing the CMS Admin Panel: Secure Login Procedure",
        intro:
          "This procedure requires adherence to the two-factor authentication (2FA) protocol to ensure platform security and integrity.",
        meta: {
          "CMS URL": "https://backend.bracbank.com/",
          "Browser Recommendation":
            "Google Chrome (Mandatory for optimal performance)",
        },
        loomUrl:
          "https://www.loom.com/share/8be296e90960413292c2154dcaf5b974",
        stepsTitle: "Steps for Authentication",
        steps: [
          "Open URL and Ensure Compliance: Navigate to the specified CMS Admin URL in the Google Chrome browser.",
          "Credential Submission: Enter your registered institutional email address and corresponding password.",
          "OTP Verification Trigger: Upon submitting credentials, the system sends a One-Time Password (OTP) to your registered mobile number as the 2FA layer.",
          "Final Authentication: Input the six-digit OTP into the verification field within the time limit. Successful authentication grants access to the main Admin Panel dashboard.",
        ],
      },
    ],
  },
  {
    chapter: 2,
    chapterTitle: "Asset Management",
    chapterAccent: "blue",
    chapterIntro:
      "The File Manager is the central repository for every image and PDF used across the live site.",
    sections: [
      {
        id: "2.1",
        title: "Uploading and Linking Assets via the File Manager",
        intro:
          "The File Manager is the central repository for all static media assets (images, PDFs). Proper asset naming is crucial for SEO and organization.",
        meta: {
          "CMS Path": "Account Settings → File Manager",
        },
        loomUrl:
          "https://www.loom.com/share/49d4fe7d30fa4520bffc1b6d9146a0ed",
        stepsTitle: "Steps for Asset Upload and Link Retrieval",
        steps: [
          "Access Module: From the dashboard, navigate to Account Settings and select File Manager.",
          "Initiate Upload: Click the Upload New File button.",
          "File Selection and Naming: Select the file from your device. Best Practice — file names must be descriptive and use hyphens instead of spaces (e.g., q3-financial-report-2025.pdf).",
          "Retrieve Secure URL: Once the upload is complete, click the contextual menu (three dots ⋯) next to the file entry and select Copy Link.",
          "Deployment: Use the copied link for hyperlinking or embedding images across the website.",
        ],
      },
    ],
  },
  {
    chapter: 3,
    chapterTitle: "Core Content Management (Products)",
    chapterAccent: "rose",
    chapterIntro:
      "Manage the heart of the bracbank.com experience — every retail and SME product page.",
    sections: [
      {
        id: "3.1",
        title: "Comprehensive Modification of Existing Product Content",
        intro:
          "This module maintains the core value propositions, features, and imagery for all banking products.",
        meta: { "CMS Path": "Products → Products (select from list)" },
        loomUrl:
          "https://www.loom.com/share/7b2cdf4b11754cfea4315e71eaa94860",
        stepsTitle: "Steps for Detailed Product Editing",
        steps: [
          "Access Product List: Navigate to Products and select Products from the menu.",
          "Locate and Edit: Search for the specific product and click the pencil (edit) icon to open the detailed editing interface.",
          "Update Main Details: Focus on the Product Name (English/Bangla), Image, Digital Connectivity (linking to Shubdha/eKYC/forms), and Call-to-Action (CTA) text/destination.",
          "Content Tab Editing: Proceed to the Tabs section to update detailed information (Features, Benefits, Eligibility Criteria).",
          "Final Submission: After verifying all updates, click the Submit button to deploy changes.",
        ],
      },
      {
        id: "3.2",
        title: "Strategic Management and Upload of Product Banners",
        intro:
          "Banners are high-impact visual elements tied to specific products.",
        meta: { "CMS Path": "Products → [Select Product] → Banner tab" },
        loomUrl:
          "https://www.loom.com/share/70ab545257124dbb94604e591ac8bf10",
        stepsTitle: "Steps for Banner Deployment",
        steps: [
          "Navigate to Banner Sub-module: Access the target product and click on the dedicated Banner tab.",
          "Input Textual Overlays: Complete the main Title and Subtitle fields.",
          "Media Upload: Upload the required asset (either a Static Image or an optimized WEBM File for subtle animation).",
          "Optimization Note: Ensure all files adhere to recommended dimensions and compression standards.",
          "Final Submission: Click the Submit button to finalize the banner upload.",
        ],
      },
    ],
  },
  {
    chapter: 4,
    chapterTitle: "Dynamic Content Updates",
    chapterAccent: "orange",
    chapterIntro:
      "Time-sensitive content — press releases, blogs, and the daily Forex rate refresh.",
    sections: [
      {
        id: "4.1",
        title: "Publishing and Managing Insights (Press Releases & Blogs)",
        intro:
          "Insights (corporate communications, press releases, articles) use a structured, two-step publication workflow.",
        meta: { "CMS Path": "Insights → Card" },
        loomUrl:
          "https://www.loom.com/share/e331e0f99ba64a888fccfc65fc0bd0ab",
        stepsTitle: "Steps for Two-Step Insight Publication",
        steps: [
          "Step 1 — Metadata Entry (Shell Creation): Go to Insights → Card and click Add New. Define metadata: Type, Date, Category (select Press Release or Blog) and Title. Click Submit.",
          "Step 2 — Content Finalization (Description Tab): Locate the newly created entry and click on the Description tab.",
          "Input the full article content using the rich-text editors for both Bangla and English fields.",
          "Ensure external assets are linked using URLs copied from the File Manager (Section 2.1).",
          "Click Submit again within the Description tab to publish the complete content.",
        ],
      },
      {
        id: "4.2",
        title: "Daily Protocol for Updating Forex Data",
        intro:
          "Foreign Exchange (Forex) rate data must be updated daily for compliance and accuracy.",
        meta: { "CMS Path": "Forex" },
        loomUrl:
          "https://www.loom.com/share/63f30c1f61384c558556c0c87d933e50",
        stepsTitle: "Steps for Daily Forex Data Refresh",
        steps: [
          "Access Forex Module: Navigate directly to the dedicated Forex section.",
          "Date Selection: Select the correct effective date for the new rate data.",
          "Regulatory File Upload: Upload the official updated Forex rate PDF file.",
          "Parameter Update: Update all text parameters and fields that drive the interactive rate display on the front end, synchronizing them with the uploaded PDF.",
          "Submission and Verification: Click Submit. Immediate front-end verification is required to confirm the update is live.",
        ],
      },
    ],
  },
  {
    chapter: 5,
    chapterTitle: "Web Settings & Landing Pages",
    chapterAccent: "yellow",
    chapterIntro:
      "Spin up custom campaign landing pages, attach banners, and manage tabular data like e-Tender / RFQ lists.",
    sections: [
      {
        id: "5.1",
        title: "Creating a New Blank Landing Page and Cache Management",
        intro:
          "The Blank Template is for developing new, custom-layout, campaign-specific landing pages.",
        meta: { "CMS Path": "Web Settings → Blank Template" },
        loomUrl:
          "https://www.loom.com/share/fa34b0e88d2c418683104d91f0e492dc",
        stepsTitle: "Steps for Landing Page Creation and Deployment",
        steps: [
          "Access Template Module: Navigate to Web Settings and select Blank Template.",
          "Initiate Creation: Click the Add New button.",
          "Input Core Page Data: Enter the required Title (English/Bangla) and main body content.",
          "Initial Submission: Click the first Submit button to save the page content and URL structure.",
          "Critical Deployment Step — Cache Clearing: You must perform the Clear Cache operation immediately after submission. Failing to clear the cache will prevent the new page from becoming visible on the live site.",
        ],
        callout: {
          type: "warning",
          text: "Forgetting Clear Cache is the #1 reason new landing pages don't appear live. Always run it right after Submit.",
        },
      },
      {
        id: "5.2",
        title: "Adding a Banner to an Existing Landing Page",
        intro:
          "Integrates a dedicated banner into a pre-existing Blank Landing Page.",
        meta: {
          "CMS Path":
            "Web Settings → Blank Template → [Search Page] → Banner",
        },
        loomUrl:
          "https://www.loom.com/share/c0b40afb1bdd4a238d9cd5ad99e50cc9",
        stepsTitle: "Steps for Banner Integration",
        steps: [
          "Locate Target Page: Find the landing page under Blank Template and click the associated Banner button.",
          "Configuration Settings: Select the required banner type (e.g., full-width, split-screen).",
          "Text and Media Input: Enter the necessary text fields and upload the corresponding media file (static image or WEBM).",
          "Final Submission: Click the Submit button.",
        ],
      },
      {
        id: "5.3",
        title: "Managing Tabular Data (e.g., e-Tender, RFQ)",
        intro:
          "Updates structured lists like procurement notices (RFQ) or official bid documents (e-Tender).",
        meta: {
          "CMS Path":
            "Web Settings → Blank Template → [Search Page] → Manage Table → Manage Rows",
        },
        loomUrl:
          "https://www.loom.com/share/95f2bd78e5294646986e880ff06c503f",
        stepsTitle: "Steps for Table Row Management (Add / Modify)",
        steps: [
          "Access Row Management: Locate the target page under Blank Template, click Manage Table, then Manage Rows.",
          "Adding a New Entry: Click Add New. Enter the item's official Title. Upload the corresponding official PDF file using the File Manager utility.",
          "Modification / Archival: Existing rows can be modified or archived / deleted via the action icons.",
          "Submission: Click Submit to finalize the data.",
        ],
      },
    ],
  },
  {
    chapter: 6,
    chapterTitle: "Site Structure & Page Content",
    chapterAccent: "blue",
    chapterIntro:
      "Every structural page on bracbank.com — Home, Retail, SME, About, Corporate, and 16 more.",
    sections: [
      {
        id: "6.1",
        title: "Home Page",
        intro:
          "The Home Page is the brand's first impression. Each sub-section is managed via the pen icon (edit) or Add New button.",
        meta: { "CMS Path": "Login → Home Page" },
        loomUrl:
          "https://www.loom.com/share/6caa0f3495084a6cbb3dffb0aa1a76c0",
        stepsTitle: "Sub-sections to Manage",
        steps: [
          "Banner — Update banners following the standard Banner Upload tutorial.",
          "Product Selector — Edit the image, title, and button name of the product selector interface.",
          "Your Needs — Click the text to view the list of icons and titles (Bangla / English). Use the pen icon to edit, or Add New for new entries.",
          "Albums and Activities — Click the text to view list of images and titles. Edit or Add New.",
          "More for You — Same workflow as Albums and Activities.",
        ],
      },
      {
        id: "6.2",
        title: "Retail Page",
        intro:
          "Clicking Retail Page expands into Landing Page, Accounts, Loan, Term Deposit, and Card.",
        meta: { "CMS Path": "Login → Retail Page" },
        loomUrl:
          "https://www.loom.com/share/3627df4dc11a414e9c8f5207fd201996",
        stepsTitle: "Sub-sections",
        steps: [
          "Landing Page — Sub-sections are managed the same way as the Home Page.",
          "Accounts / Loan / Term Deposit / Card → Banner — Follow the standard banner upload instructions.",
          "Accounts / Loan / Term Deposit / Card → Product — Map newly added products and modify the sorting order via drag-and-drop.",
          "Accounts / Loan / Term Deposit / Card → Related Items — Search within the box to add new products or delete current ones.",
        ],
      },
      {
        id: "6.3",
        title: "SME Page",
        intro:
          "SME Page structure mirrors the Retail Page exactly.",
        meta: { "CMS Path": "Login → SME Page" },
        loomUrl:
          "https://www.loom.com/share/93ed2304183c494ea7e405ac722adfd9",
        stepsTitle: "Sub-sections",
        steps: [
          "Landing Page — Managed the same way as the Home Page.",
          "Accounts, Loan, Term Deposit, Card — Banner / Product / Related Items management is identical to the Retail Page.",
        ],
      },
      {
        id: "6.4",
        title: "About Page",
        intro:
          "Expands into Company Profile and CSR.",
        meta: { "CMS Path": "Login → About Page" },
        loomUrl:
          "https://www.loom.com/share/9e38c53c28df485ea1a8c0a1d7d38679",
        stepsTitle: "Sub-sections",
        steps: [
          "Company Profile — Banner, At a Glance, Contents, Achievements, Subsidiaries, Proud Members. Edit via pen icon or Add New.",
          "CSR — Banner, Content, Ethos, and Tab. Use pen icon to edit each element.",
        ],
      },
      {
        id: "6.5",
        title: "Corporate Page",
        intro:
          "Expands into Landing Page and Governance Compliance.",
        meta: { "CMS Path": "Login → Corporate Page" },
        loomUrl:
          "https://www.loom.com/share/14fdcca4c76f4abe9e6c9b8f91d20cf2",
        stepsTitle: "Sub-sections",
        steps: [
          "Landing Page — Managed the same way as Home Page landing sections.",
          "Governance and Compliance — Banner and Content. Click text and use the pen icon to change information.",
        ],
      },
      {
        id: "6.6",
        title: "Agent Banking Page",
        intro:
          "Manage Agent Banking copy, dynamic lead forms, and videos.",
        meta: { "CMS Path": "Login → Agent Banking Page" },
        loomUrl:
          "https://www.loom.com/share/5f5fe13aeefc445db68d76dc9dcd48bd",
        stepsTitle: "Sub-sections",
        steps: [
          "Content — Use the editor to type / update text in both Bangla and English.",
          "Form Section — Use Manage Form to create a dynamic form. Use the pen icon after clicking Manage Rows to update field names.",
          "Video Section — Update or replace video URLs, images, and titles.",
          "Form Submission — View and export form responses / leads created in the Form section.",
        ],
      },
      {
        id: "6.7",
        title: "Important Information Page",
        intro: "Banner, content, and related items.",
        meta: { "CMS Path": "Login → Important Information" },
        loomUrl:
          "https://www.loom.com/share/3b6ac2717de14dbcbe97c75437f1ccb2",
        stepsTitle: "Sub-sections",
        steps: [
          "Banner — Follow standard banner upload instructions.",
          "Content — Use the editor to type / update text in both Bangla and English.",
          "Related Items — Search within the box to map new products or delete current ones.",
        ],
      },
      {
        id: "6.8",
        title: "Digital Service Page",
        intro:
          "Astha, Corpnet, Share Management, SMS Banking, and E-Commerce sub-sections.",
        meta: { "CMS Path": "Login → Digital Service Page" },
        loomUrl:
          "https://www.loom.com/share/5bc4363401724032b3bdb44e8848c98d",
        stepsTitle: "Sub-sections",
        steps: [
          "Landing Page — Managed the same way as the Home Page.",
          "Astha / Corpnet / Share Management / SMS Banking / E-Commerce — Each contains Banner, Tab, Video, Related Offers, Video Gallery, and Registration. Click on text and use pen icon or Add New to modify content / URLs.",
        ],
      },
      {
        id: "6.9",
        title: "Treasury and FI Page",
        intro: "Treasury & Financial Institution landing.",
        meta: { "CMS Path": "Login → Treasury & FI" },
        loomUrl:
          "https://www.loom.com/share/7af68bab1c95428692532b53b1636054",
        stepsTitle: "Sub-sections",
        steps: [
          "Retail Landing — Similar to Retail / SME landing pages, with subsections like Banner, Your Needs, and Our Activities. Edit via pen icon or Add New.",
        ],
      },
      {
        id: "6.10",
        title: "Special Segment Page",
        intro: "Audience-specific landing flow.",
        meta: { "CMS Path": "Login → Special Segment" },
        loomUrl:
          "https://www.loom.com/share/ea53062d0e734dc2a30e3a2727dc882c",
        stepsTitle: "Sub-sections",
        steps: [
          "Retail Landing — Similar to Retail / SME landing pages, with Banner, Your Needs, and Our Activities. Edit using the same steps as the Retail section.",
        ],
      },
      {
        id: "6.11",
        title: "Premium Banking Page",
        intro: "High-net-worth segment content.",
        meta: { "CMS Path": "Login → Premium Banking" },
        loomUrl:
          "https://www.loom.com/share/5c1119258d3c4b33bbee53e3c16a07c5",
        stepsTitle: "Sub-sections",
        steps: [
          "Landing Page — Follow update steps for Retail or Home Page landing sections.",
          "PB Product — View the list of Premium Banking products. Add New to include a product, or pen icon to edit image and text.",
          "PB Category — Update categories as needed.",
        ],
      },
      {
        id: "6.12",
        title: "Compare Cards Features",
        intro:
          "Add or edit the feature comparison rows shown on card product pages.",
        meta: { "CMS Path": "Login → Product → Description" },
        loomUrl:
          "https://www.loom.com/share/93fc3eaafd9445dfa747ae34d6dbd7c4",
        stepsTitle: "Steps",
        steps: [
          "Navigate to the Products section.",
          "Search and find the desired product, then click the pen icon.",
          "Go to the Description tab. At the bottom find Card comparison features.",
          "Use Add New to create new blank sections for comparison lines / features, or edit existing text from the same place.",
        ],
      },
      {
        id: "6.13",
        title: "Offer Section Management",
        intro: "Run promotional offers, merchants, and categories.",
        meta: { "CMS Path": "Login → Offer" },
        loomUrl:
          "https://www.loom.com/share/016b047b650a4fa1ab0a5f36315119cc",
        stepsTitle: "Sub-sections",
        steps: [
          "Banner — Follow standard banner upload instructions.",
          "Offer — Click to view list of existing offers. Use pen icon to edit. Click Add New, fill in image / title / template option (Single merchant, Multi merchant single offer, Multi merchant multi offer). Set start and end dates.",
          "Offer Category, Offer Attribute, Merchant, Merchants Category — Click each text field and use pen icon or Add New to manage these lists.",
        ],
      },
      {
        id: "6.14",
        title: "Share Holding Structure & Board of Directors",
        intro: "Investor relations content.",
        meta: {
          "CMS Path": "Login → Share Holding Structure & Board Director",
        },
        loomUrl:
          "https://www.loom.com/share/83b4e997a6d44f60a3ef91261d721bd8",
        stepsTitle: "Steps",
        steps: [
          "Navigate to Share Holding Structure or Board of Director.",
          "Click on the respective section.",
          "Use the pen icon to edit existing information or Add New for new entries (e.g., new director details). Use the edit box to update textual information.",
        ],
      },
      {
        id: "6.15",
        title: "Management Committee",
        intro: "Executive committee roster page.",
        meta: { "CMS Path": "Login → Management Committee" },
        loomUrl:
          "https://www.loom.com/share/a26d2e8d7a104eddb708987b3ac76f67",
        stepsTitle: "Steps",
        steps: [
          "Navigate to Management Committee.",
          "The current member list is displayed.",
          "Use the pen icon to edit a member's name, designation, bio, and image.",
          "Use Add New to add a new committee member.",
        ],
      },
      {
        id: "6.16",
        title: "Related Items",
        intro: "Cross-link products globally.",
        meta: { "CMS Path": "Login → Related Items" },
        loomUrl:
          "https://www.loom.com/share/6f138b195a7746a688c7735b5e4e9c25",
        stepsTitle: "Steps",
        steps: [
          "Navigate to Related Items.",
          "The current list of products linked as related items is displayed.",
          "Use the pen icon to modify current content.",
          "Use Add New to add new products to the list.",
        ],
      },
      {
        id: "6.17",
        title: "Last Minute Deal",
        intro: "Promotional deal slots.",
        meta: { "CMS Path": "Login → Last Minute Deal" },
        loomUrl:
          "https://www.loom.com/share/9813da92ebbe45aeb31f85aa6a461f6e",
        stepsTitle: "Steps",
        steps: [
          "Navigate to Last Minute Deal.",
          "The list of existing offers is displayed.",
          "Use the pen icon to edit current offer information.",
          "Use Add New to add new offers to this section.",
        ],
      },
      {
        id: "6.18",
        title: "FAQ Page",
        intro: "Global FAQ editor for the entire website.",
        meta: { "CMS Path": "Login → FAQ" },
        loomUrl:
          "https://www.loom.com/share/83af5b1c3a264a2ba9123319e71c0703",
        stepsTitle: "Steps",
        steps: [
          "Navigate to the FAQ subsection (under Product or other main sections, as configured).",
          "The current main FAQ questions for the entire website are displayed.",
          "Use the pen icon to edit and modify existing questions and answers.",
          "Use Add New to add new FAQ entries.",
        ],
      },
      {
        id: "6.19",
        title: "Contact Us Page",
        intro: "Get-in-touch info, banners, subjects, and contacts.",
        meta: { "CMS Path": "Login → Contact Us" },
        loomUrl:
          "https://www.loom.com/share/27d23e2d6c57489b954d945ae4823d42",
        stepsTitle: "Sub-sections",
        steps: [
          "Get In Touch & Contact Info — Click the texts and modify the current information directly within the text boxes.",
          "Banner Section — Follow standard banner upload instructions.",
          "Subjects & Contacts — Use pen icon to edit existing entries or Add New to create new ones.",
        ],
      },
      {
        id: "6.20",
        title: "Our Location Page",
        intro: "Branch and ATM locator data — supports bulk import.",
        meta: { "CMS Path": "Login → Our Location" },
        loomUrl:
          "https://www.loom.com/share/96d79ad2055c48259f01edab499f2e74",
        stepsTitle: "Sub-sections",
        steps: [
          "Location Category — Use pen icon to edit existing categories or Add New to create a new category.",
          "Locations — View all locations. Use pen icon or Add New to manage data. Use the Import button for bulk location data updates via an Excel sheet.",
          "Branches — View the list of branches. Use pen icon and Add New to edit or add new branch information.",
        ],
      },
      {
        id: "6.21",
        title: "TARA Section",
        intro: "Women's banking proposition pages.",
        meta: { "CMS Path": "Login → TARA" },
        loomUrl:
          "https://www.loom.com/share/d228679ccad64147bea184e55175408d",
        stepsTitle: "Steps",
        steps: [
          "The TARA section's structure (Landing Page, Retail Page, SME Page) is a direct replica of the standard Retail and SME sections, but dedicated solely to TARA content.",
          "Follow the existing instructions for those respective pages.",
        ],
      },
    ],
  },
  {
    chapter: 7,
    chapterTitle: "Website Administration Utilities",
    chapterAccent: "rose",
    chapterIntro:
      "Search index, legacy URL redirects, and role-based admin access.",
    sections: [
      {
        id: "7.1",
        title: "Managing Website Search Tags",
        intro:
          "Manages the internal website search results — what users see when they search the live bracbank.com.",
        meta: {
          "CMS Path": "Login → Web Settings → Manage Website Search",
        },
        loomUrl:
          "https://www.loom.com/share/bb3978903a7f442e8e2b1689d5237eed",
        stepsTitle: "Steps",
        steps: [
          "Enlarge the Web Settings section and click Manage Website Search.",
          "The current list of search entries is displayed.",
          "To add a new page to the search index, click Add New.",
          "Set a Title (English and Bangla) and, most importantly, provide the target Page URL. When a user searches the title, they will be redirected to this URL.",
        ],
      },
      {
        id: "7.2",
        title: "Redirecting Legacy URLs",
        intro:
          "Manages redirects from old / legacy URLs to the current website structure.",
        meta: { "CMS Path": "Login → Web Settings → Redirect URL" },
        loomUrl:
          "https://www.loom.com/share/5f82dc902cf042ac8b3e56692f0d0354",
        stepsTitle: "Steps",
        steps: [
          "Find Web Settings → Redirect URL.",
          "The existing list of redirects is displayed.",
          "To edit an existing redirect, click the pen icon and modify the Source URL (old / legacy URL) and Destination URL (new, current URL).",
          "To create a new redirect, click Add New and input both the Source and Destination URLs.",
        ],
      },
      {
        id: "7.3",
        title: "Creating New Users and Assigning Roles / Permissions",
        intro:
          "Provision administrative access — roles, permissions, and user accounts.",
        meta: { "CMS Path": "Login → Account Settings → Role" },
        loomUrl:
          "https://www.loom.com/share/ee88e539ea4b4cd18c786920c361ba0b",
        stepsTitle: "Steps",
        steps: [
          "Find Account Settings and click Role and Permission.",
          "Use the Role portion to create new administrative roles (e.g., system admin, forex admin). Click Add New to create a role.",
          "For Permission, click the appropriate checkboxes to assign access levels, or use Select All Permission to grant full access based on the defined role.",
          "Use the User List section to create new user accounts and assign them one of the defined roles.",
        ],
      },
    ],
  },
  {
    chapter: 8,
    chapterTitle: "Product Selector Tree",
    chapterAccent: "orange",
    chapterIntro:
      "Customize the guided journey users take to discover the right BRAC Bank product.",
    sections: [
      {
        id: "8.1",
        title: "Product Selector Tree Update",
        intro:
          "Edit nodes, attach products, and tie eKYC / Shubdha flags to journey endpoints.",
        meta: {
          "CMS Path": "Login → Product Selector → Product Selector Tree",
        },
        loomUrl:
          "https://www.loom.com/share/ee88e539ea4b4cd18c786920c361ba0b",
        stepsTitle: "Steps",
        steps: [
          "Find Product Selector and click Product Selector Tree.",
          "Use the Tree portion to update information (e.g., Add child, edit current data). Click Add Child to add a sub-journey; click Edit to modify current node data.",
          "Click on Add Product of any specific node to connect the product shown when users reach the end of the journey. Consider and select Ekyc and Shubidha from the checkbox.",
          "Collect API keywords from BRAC Bank to update the API keywords.",
        ],
      },
    ],
  },
  {
    chapter: 9,
    chapterTitle: "Burger Menu Tree",
    chapterAccent: "yellow",
    chapterIntro: "Manage the global navigation menu hierarchy.",
    sections: [
      {
        id: "9.1",
        title: "Burger Menu Tree Update",
        intro:
          "Add, edit, and delete navigation nodes in the global burger menu.",
        meta: { "CMS Path": "Login → Web Settings → Menu Management" },
        loomUrl:
          "https://www.loom.com/share/ef5676fe291a449992b54ce507b3fe86",
        stepsTitle: "Steps",
        steps: [
          "Find Web Settings and click Menu Management.",
          "Use the Tree portion to update information (e.g., Add sub-menu, edit current data).",
          "Click Plus (+) to add a sub-menu, click Pen to modify current node data. To delete a node click the cross (×).",
        ],
      },
    ],
  },
  {
    chapter: 10,
    chapterTitle: "Form Builder",
    chapterAccent: "blue",
    chapterIntro:
      "Create dynamic lead-capture forms attached to specific products.",
    sections: [
      {
        id: "10.1",
        title: "Creating a Product Form",
        intro:
          "Build new product-specific forms with custom inputs via the form builder.",
        meta: {
          "CMS Path":
            "Login → Products → Product → Form Builder → Add New → Manage Form → Add Input",
        },
        loomUrl:
          "https://www.loom.com/share/4aa7c972de3f460885a0e265d09c9ebf",
        stepsTitle: "Steps",
        steps: [
          "Find Products and click Product.",
          "Go to the edit option and find the Form Builder.",
          "Add a new form, then click Manage Form.",
          "Add inputs to the form (fields, labels, validations).",
        ],
      },
    ],
  },
  {
    chapter: 11,
    chapterTitle: "Career Page",
    chapterAccent: "rose",
    chapterIntro: "Employer branding and current openings.",
    sections: [
      {
        id: "11.1",
        title: "Updating the Career Landing Page",
        intro: "Edit hero, value props, and any landing sections.",
        meta: { "CMS Path": "Login → Career → Landing Page" },
        loomUrl:
          "https://www.loom.com/share/5f6f0dac5709454fbd323959d7236370",
        stepsTitle: "Steps",
        steps: [
          "Find Career and click Landing page.",
          "You can add, edit, or update any information in landing page sections.",
        ],
      },
    ],
  },
  {
    chapter: 12,
    chapterTitle: "Footer",
    chapterAccent: "orange",
    chapterIntro: "Site-wide footer links, contact information, and legal.",
    sections: [
      {
        id: "12.1",
        title: "Managing the Site Footer",
        intro:
          "Update every column of the global footer including links and contact info.",
        meta: { "CMS Path": "Login → Web Settings → Manage Footer" },
        loomUrl:
          "https://www.loom.com/share/ddb009ef63b445baad927aee31a589ae",
        stepsTitle: "Steps",
        steps: [
          "Find Web Settings and click Manage Footer.",
          "You can add, edit, or update any information in the footer (columns, links, addresses, contact email).",
        ],
        callout: {
          type: "info",
          text: "For any queries please email us at info@singularitybd.com",
        },
      },
    ],
  },
];

export const chapters = raw.map((c) => ({
  ...c,
  slug: `chapter-${c.chapter}-${slugify(c.chapterTitle)}`,
  sections: c.sections.map((s) => ({
    ...s,
    chapter: c.chapter,
    chapterTitle: c.chapterTitle,
    chapterAccent: c.chapterAccent,
    slug: `${s.id.replace(".", "-")}-${slugify(s.title)}`,
  })),
}));

export const allTutorials = chapters.flatMap((c) => c.sections);

export const findTutorial = (slug) =>
  allTutorials.find((t) => t.slug === slug) || null;

export const findChapter = (chapterNum) =>
  chapters.find((c) => c.chapter === Number(chapterNum)) || null;

export const accentClass = {
  yellow: "bg-brut-yellow text-foreground",
  rose: "bg-brut-rose text-foreground",
  blue: "bg-brut-blue text-foreground",
  orange: "bg-brut-orange text-foreground",
};

export const accentBg = {
  yellow: "hsl(var(--brut-yellow))",
  rose: "hsl(var(--brut-rose))",
  blue: "hsl(var(--brut-blue))",
  orange: "hsl(var(--brut-orange))",
};
